import type { ToolsRepositoryInterface } from '@/api/interfaces/tools/toolsRepository';
import { staticImplements } from '@/decorators/staticImplements';
import { httpClient } from '../httpClient/httpClient';
import { BakeApplicationSummariesApplicationsResponse } from '@/api/interfaces/tools/bakeApplicationSummariesApplicationsResponse';
import { ExportJsonConfig } from '@/api/interfaces/tools/exportJsonConfig';
import { ExportJsonDateRange } from '@/api/interfaces/tools/exportJsonDateRange';
import { ExportJsonPreviewResult } from '@/api/interfaces/tools/exportJsonPreviewResult';
import { InstallerCidaasSubOverrideResponse } from '@/api/interfaces/tools/installerCidaasSubOverrideResponse';
import { InstallersDataResponse } from '@/api/interfaces/tools/installersDataResponse';

@staticImplements<ToolsRepositoryInterface>()
export class ToolsRepository {
    static bakeApplicationSummariesFetchApplications(): Promise<
        BakeApplicationSummariesApplicationsResponse
    > {
        return httpClient.get<BakeApplicationSummariesApplicationsResponse>(
            '/api/dea/v1/tools/bakeApplicationSummaries',
        );
    }

    static bakeApplicationSummariesProcessApplications(
        limit: number,
    ): Promise<BakeApplicationSummariesApplicationsResponse> {
        return httpClient.get<BakeApplicationSummariesApplicationsResponse>(
            `/api/dea/v1/tools/bakeApplicationSummaries/${limit}`,
        );
    }

    static bakeApplicationSummariesDeleteApplications(): Promise<
        BakeApplicationSummariesApplicationsResponse
    > {
        return httpClient.delete<BakeApplicationSummariesApplicationsResponse>(
            '/api/dea/v1/tools/bakeApplicationSummaries',
        );
    }

    static previewExportJson(
        config: ExportJsonConfig,
    ): Promise<ExportJsonPreviewResult> {
        return httpClient.post<ExportJsonPreviewResult>(
            '/api/dea/v1/tools/exportJson/preview',
            {
                data: config,
            },
        );
    }

    static getExportJsonDateRange(): Promise<ExportJsonDateRange> {
        return httpClient.get<ExportJsonDateRange>(
            '/api/dea/v1/tools/exportJson/dateRange',
        );
    }

    /**
     * Downloads the export file. The response is gzip encoded and
     * transparently decoded by the browser, so the file is plain NDJSON.
     */
    static async downloadExportJson(config: ExportJsonConfig): Promise<void> {
        const blob = await httpClient.post<Blob>(
            '/api/dea/v1/tools/exportJson',
            {
                data: config,
                responseType: 'blob',
            },
        );

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `dea-export-${new Date()
            .toISOString()
            .slice(0, 10)}.ndjson`;
        link.click();
        URL.revokeObjectURL(url);
    }

    static getInstallers(): Promise<InstallersDataResponse> {
        return httpClient.get<InstallersDataResponse>(
            '/api/dea/v1/tools/installers',
        );
    }

    static overrideInstallerCidaasSub(
        installerId: string,
        cidaasSub: string,
    ): Promise<InstallerCidaasSubOverrideResponse> {
        return httpClient.post<InstallerCidaasSubOverrideResponse>(
            '/api/dea/v1/tools/installers/cidaasSub',
            { data: { installerId, cidaasSub } },
        );
    }
}
